import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { BehaviorSubject, Observable, forkJoin, merge, switchMap, tap } from 'rxjs';
import { withPdfPages$ } from '../pdf-pages/with-pdf-pages';
import { BaseComponent } from '../components/base/base.component';
import { PatientService } from './patient.service';
import { SocketService } from './socket.service';
import { GroupService } from './group.service';

export interface BillingDocument {
  id: string;
  patientId: string;
  groupId: string;
  userId: string;
  providerId?: string;
  billingNumber: string;
  documentType: 'estimate' | 'invoice' | 'receipt';
  status: 'draft' | 'pending' | 'paid' | 'partially_paid' | 'overdue' | 'cancelled';
  issueDate: Date;
  dueDate?: Date;
  subtotal: number;
  vat: number;
  totalDue: number;
  amountPaid: number;
  outstandingBalance: number;
  vatExclusive?: boolean;
  /** VAT rate in force at issue. Null/absent on documents predating VAT gating — derive from the group. */
  vatRate?: number | null;
  notes?: string;
  pdfUrl?: string;
  pdfFileName?: string;
  priceGroupId?: string;
  ruleType: string;
  discount: number;
  discountType: 'percentage' | 'fixed';
  paymentId?: string;
  active: boolean;
  createdAt: Date;
  updatedAt: Date;
  patient?: any;
  group?: any;
  user?: any;
  Provider?: any;
  doctorTariff?: any;
  lineItems?: BillingLineItem[];
  paymentAllocations?: PaymentAllocation[];
  PatientPaymentAllocations?: PaymentAllocation[];
  PatientPayment?: PatientPayment;
  Patient?: any;
  appointmentId?: string;
  media?: Array<{
    id: string;
    mediaType: string;
    mediaUrl: string;
    mediaFileName: string;
    originalPdfUrl?: string;
    displayIndex?: number;
    active?: boolean;
    processingStatus?: 'processing' | 'completed' | 'failed';
  }>;
}

export interface BillingLineItem {
  id: string;
  billingDocumentId: string;
  code: string;
  description?: string;
  codeType: 'procedure' | 'medication' | 'material';
  quantity: number;
  unitPrice: number;
  patientSchemeRate: number;
  doctorTariffRate: number;
  shortfall: number;
  discount: number;
  discountType: 'percentage' | 'fixed';
  totalAmount: number;
  icdCodes?: string[];
  modifiers?: string[];
  date: Date;
  startTime?: string;
  endTime?: string;
  weight?: string;
  height?: string;
  BMI?: string;
  ruleTypeOverride?: string;
  providerId?: string | null;
  Provider?: { id: string; title?: string; name?: string; surname?: string; mpNumber?: string } | null;
  displayIndex?: number;
  active: boolean;
}

export interface PatientPayment {
  id: string;
  patientId: string;
  groupId: string;
  userId: string;
  paymentNumber: string;
  paymentDate: Date;
  paymentMethod: 'cash' | 'card' | 'eft' | 'medical_aid' | 'credit';
  amountReceived: number;
  reference?: string;
  notes?: string;
  receiptUrl?: string;
  receiptFileName?: string;
  yocoPaymentId?: string;
  active: boolean;
  patient?: any;
  group?: any;
  user?: any;
  paymentAllocations?: PaymentAllocation[];
}

export interface PaymentAllocation {
  id: string;
  paymentId: string;
  billingDocumentId: string;
  allocatedAmount: number;
  active: boolean;
  payment?: PatientPayment;
  billingDocument?: BillingDocument;
}

export interface PatientCredit {
  id: string;
  patientId: string;
  groupId: string;
  amount: number;
  source: 'overpayment' | 'refund' | 'adjustment';
  reference?: string;
  notes?: string;
  paymentId?: string;
  billingDocumentId?: string;
  active: boolean;
}

export interface PatientBalance {
  totalOutstanding: number;
  totalCredits: number;
  netBalance: number;
  billingDocuments: number;
  credits: number;
}

export interface PaymentSummary {
  paymentMethods: Array<{
    paymentMethod: string;
    totalAmount: number;
    count: number;
  }>;
  totalPayments: number;
  recentPayments: PatientPayment[];
}

@Injectable({
  providedIn: 'root'
})
export class PatientFinancialService {
  private apiUrl = 'api';

  private billingDocumentsSubject = new BehaviorSubject<BillingDocument[]>([]);
  private paymentsSubject = new BehaviorSubject<PatientPayment[]>([]);
  private patientBalanceSubject = new BehaviorSubject<PatientBalance | null>(null);

  billingDocuments$ = this.billingDocumentsSubject.asObservable();
  payments$ = this.paymentsSubject.asObservable();
  patientBalance$ = this.patientBalanceSubject.asObservable();

  private patientId?: string;

  constructor(
    private http: HttpClient,
    private patientService: PatientService,
    private socketService: SocketService,
    private groupService: GroupService
  ) {
    this.subscribeToCurrentPatient();
    this.setupSocketListeners();
  }

  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }

  private subscribeToCurrentPatient(): void {
    this.patientService.currentPatient$.pipe(
      switchMap(patient => {
        if (patient) {
          this.patientId = patient.id;
          this.billingDocumentsSubject.next([]);
          this.paymentsSubject.next([]);
          this.patientBalanceSubject.next(null);
          return forkJoin([
            this.loadPatientBillingDocuments(patient.id),
            this.loadPatientPayments(patient.id),
            this.getPatientBalance(patient.id)
          ]);
        } else {
          // If there's no current patient, clear the data
          this.patientId = undefined;
          this.billingDocumentsSubject.next([]);
          this.paymentsSubject.next([]);
          this.patientBalanceSubject.next(null);
          return []; // Return an empty array to satisfy the Observable return type
        }
      })
    ).subscribe();
  }

  private setupSocketListeners(): void {
    // Current patient's ID
    let currentPatientId: string | undefined = undefined;

    // Subscribe to the current patient changes to keep track of the current patient's ID
    this.patientService.currentPatient$.subscribe(patient => {
      currentPatientId = patient ? patient.id : undefined;
      this.patientId = currentPatientId;
    });

    // Listener for when a new billing document is created
    this.socketService.listen('billingDocumentCreated', 'patient-financial-service').subscribe((billingDocument: any) => {
      if (billingDocument.patientId === currentPatientId) {
        const currentBillingDocuments = this.billingDocumentsSubject.getValue() || [];
        // Check if document already exists (from optimistic update)
        const existingIndex = currentBillingDocuments.findIndex(doc => doc.id === billingDocument.id);
        if (existingIndex >= 0) {
          // Update existing document with complete data from server (may have more associations)
          currentBillingDocuments[existingIndex] = billingDocument;
          this.billingDocumentsSubject.next([...currentBillingDocuments]);
        } else {
          // Add new document at the beginning
          this.billingDocumentsSubject.next([billingDocument, ...currentBillingDocuments]);
        }
      }
    });

    // Listener for when a billing document is updated
    this.socketService.listen('billingDocumentUpdated', 'patient-financial-service').subscribe((updatedBillingDocument: any) => {
      if (updatedBillingDocument.patientId === currentPatientId) {
        const currentBillingDocuments = this.billingDocumentsSubject.getValue() || [];
        const updatedBillingDocuments = currentBillingDocuments.map(doc => {
          if (doc.id === updatedBillingDocument.id) {
            // Preserve processingStatus from existing media when updating
            const existingMedia = doc.media || [];
            const updatedMedia = (updatedBillingDocument.media || []).map((newMedia: any) => {
              const existingMediaItem = existingMedia.find((m: any) => m.id === newMedia.id);
              if (existingMediaItem && existingMediaItem.processingStatus === 'processing') {
                return { ...newMedia, processingStatus: existingMediaItem.processingStatus };
              }
              return newMedia;
            });
            return {
              ...updatedBillingDocument,
              media: updatedMedia
            };
          }
          return doc;
        });
        this.billingDocumentsSubject.next(updatedBillingDocuments);
      }
    });

    // Listener for when a billing document is deleted
    this.socketService.listen('billingDocumentDeleted', 'patient-financial-service').subscribe((deletedBillingDocument: any) => {
      if (deletedBillingDocument.patientId === currentPatientId) {
        const currentBillingDocuments = this.billingDocumentsSubject.getValue() || [];
        const updatedBillingDocuments = currentBillingDocuments.filter(doc => doc.id !== deletedBillingDocument.id);
        this.billingDocumentsSubject.next(updatedBillingDocuments);
      }
    });

    // Listener for when a billing document is removed (soft delete)
    this.socketService.listen('billingDocumentRemoved', 'patient-financial-service').subscribe((removedBillingDocument: any) => {
      if (removedBillingDocument.patientId === currentPatientId) {
        //console.log('Socket: Billing document removed', removedBillingDocument);
        const currentBillingDocuments = this.billingDocumentsSubject.getValue() || [];
        const updatedBillingDocuments = currentBillingDocuments.map(doc =>
          doc.id === removedBillingDocument.id ? { ...doc, ...removedBillingDocument } : doc
        );
        this.billingDocumentsSubject.next(updatedBillingDocuments);
      }
    });

    // Listener for when a billing document is removed with payments (dangerous action)
    this.socketService.listen('billingDocumentRemovedWithPayments', 'patient-financial-service').subscribe((removedBillingDocument: any) => {
      if (removedBillingDocument.patientId === currentPatientId) {
        //console.log('Socket: Billing document removed with payments', removedBillingDocument);
        const currentBillingDocuments = this.billingDocumentsSubject.getValue() || [];
        
        // Update the main billing document
        const updatedBillingDocuments = currentBillingDocuments.map(doc =>
          doc.id === removedBillingDocument.id ? { ...doc, ...removedBillingDocument } : doc
        );
        
        // Also update any associated receipts that were removed
        if (removedBillingDocument.receiptsRemovedIds && removedBillingDocument.receiptsRemovedIds.length > 0) {
          //console.log('Socket: Updating removed receipts', removedBillingDocument.receiptsRemovedIds);
          const finalBillingDocuments = updatedBillingDocuments.map(doc => {
            if (removedBillingDocument.receiptsRemovedIds.includes(doc.id)) {
              return {
                ...doc,
                removedAt: removedBillingDocument.removedAt,
                removedBy: removedBillingDocument.removedBy,
                removalReason: `Associated invoice removed: ${removedBillingDocument.removalReason}`,
                active: false,
                paymentsRemoved: true
              };
            }
            return doc;
          });
          this.billingDocumentsSubject.next(finalBillingDocuments);
        } else {
          this.billingDocumentsSubject.next(updatedBillingDocuments);
        }
      }
    });

    // Listener for when a new payment is created
    this.socketService.listen('patientPaymentCreated', 'patient-financial-service').subscribe((payment: any) => {
      if (payment.patientId === currentPatientId) {
        const currentPayments = this.paymentsSubject.getValue() || [];
        currentPayments.unshift(payment);
        this.paymentsSubject.next(currentPayments);
      }
    });

    // Listener for when a payment is updated
    this.socketService.listen('patientPaymentUpdated', 'patient-financial-service').subscribe((updatedPayment: any) => {
      if (updatedPayment.patientId === currentPatientId) {
        const currentPayments = this.paymentsSubject.getValue() || [];
        const updatedPayments = currentPayments.map(payment =>
          payment.id === updatedPayment.id ? updatedPayment : payment
        );
        this.paymentsSubject.next(updatedPayments);
      }
    });

    // Listener for when a payment is deleted
    this.socketService.listen('patientPaymentDeleted', 'patient-financial-service').subscribe((deletedPayment: any) => {
      if (deletedPayment.patientId === currentPatientId) {
        const currentPayments = this.paymentsSubject.getValue() || [];
        const updatedPayments = currentPayments.filter(payment => payment.id !== deletedPayment.id);
        this.paymentsSubject.next(updatedPayments);
      }
    });

    // Listener for when a patient credit is created, applied to an invoice or given back
    merge(
      this.socketService.listen('patientCreditCreated', 'patient-financial-service'),
      this.socketService.listen('patientCreditApplied', 'patient-financial-service'),
    ).subscribe((credit: any) => {
      if (credit.patientId === currentPatientId) {
        // Refresh patient balance when credits change
        if (currentPatientId) {
          this.getPatientBalance(currentPatientId).subscribe();
        }
      }
    });

    // Listener for when billing PDF conversion starts
    this.socketService.listen('billingPdfConversionStarted', 'patient-financial-service').subscribe((data: any) => {
      if (data.patientId === currentPatientId && data.mediaId) {
        const currentBillingDocuments = this.billingDocumentsSubject.getValue() || [];
        const docIndex = currentBillingDocuments.findIndex(doc => doc.id === data.billingDocumentId);
        
        if (docIndex >= 0) {
          const mediaList = currentBillingDocuments[docIndex].media as any[] || [];
          const mediaIndex = mediaList.findIndex(m => m.id === data.mediaId);
          
          if (mediaIndex >= 0) {
            // Mark PDF as processing
            mediaList[mediaIndex] = {
              ...mediaList[mediaIndex],
              processingStatus: 'processing'
            };
            currentBillingDocuments[docIndex] = {
              ...currentBillingDocuments[docIndex],
              media: mediaList
            };
            this.billingDocumentsSubject.next([...currentBillingDocuments]);
          }
        }
      }
    });

    // Listener for when billing PDF conversion completes
    this.socketService.listen('billingPdfConversionCompleted', 'patient-financial-service').subscribe((data: any) => {
      if (data.patientId === currentPatientId) {
        // The billingDocumentUpdated event will handle the full update with new images
        // This event is mainly for tracking/logging purposes
        // We could also update processing status here if needed
        const currentBillingDocuments = this.billingDocumentsSubject.getValue() || [];
        const docIndex = currentBillingDocuments.findIndex(doc => doc.id === data.itemId);
        
        if (docIndex >= 0) {
          const mediaList = currentBillingDocuments[docIndex].media as any[] || [];
          // Remove processing status from PDF media
          const updatedMedia = mediaList.map(m => {
            if (m.mediaType === 'application/pdf' && m.processingStatus === 'processing') {
              return { ...m, processingStatus: undefined };
            }
            return m;
          });
          currentBillingDocuments[docIndex] = {
            ...currentBillingDocuments[docIndex],
            media: updatedMedia
          };
          this.billingDocumentsSubject.next([...currentBillingDocuments]);
        }
      }
    });
  }

  // Billing Document Methods
  loadPatientBillingDocuments(
    patientId: string,
    options?: {
      documentType?: string;
      status?: string;
      page?: number;
      pageSize?: number;
    }
  ): Observable<{ data: BillingDocument[]; pagination: any }> {
    const params: any = {};
    if (options?.documentType) params.documentType = options.documentType;
    if (options?.status) params.status = options.status;
    if (options?.page) params.page = options.page.toString();
    if (options?.pageSize) params.pageSize = options.pageSize.toString();

    return this.http.get<{ data: BillingDocument[]; pagination: any }>(
      `${this.apiUrl}/billing-documents/patient/${patientId}`,
      { params, headers: this.getAuthHeaders() }
    ).pipe(
      tap(response => {
        this.billingDocumentsSubject.next(response.data);
      })
    );
  }

  createBillingDocument(data: {
    patientId: string;
    groupId: string;
    documentType: 'estimate' | 'invoice' | 'receipt' | 'statement';
    lineItems: any[];
    notes?: string;
    issueDate: Date;
    dueDate?: Date;
    priceGroupId?: string;
    ruleType?: string;
    discount?: number;
    discountType?: 'percentage' | 'fixed';
    subtotal?: number;
    vatAmount?: number;
    totalAmount?: number;
    billingNumber?: string;
    paymentId?: string;
    appointmentId?: string;
    providerId?: string;
    originalInvoiceNumber?: string;
    vatExclusive?: boolean;
    vatRate?: number | null;
  }): Observable<{ success: boolean, data: BillingDocument }> {
    return this.http.post<{ success: boolean, data: BillingDocument }>(`${this.apiUrl}/billing-documents`, data, {
      headers: this.getAuthHeaders()
    }).pipe(
      tap(response => {
        // Optimistically add the created document to the local array immediately
        // This ensures it's available for statement generation even if socket event is delayed
        if (response.success && response.data && response.data.patientId === this.patientId) {
          const currentBillingDocuments = this.billingDocumentsSubject.getValue() || [];
          // Check if document already exists (from socket event that arrived first)
          const existingIndex = currentBillingDocuments.findIndex(doc => doc.id === response.data.id);
          if (existingIndex >= 0) {
            // Update existing document - merge to preserve any associations that might already exist
            const existingDoc = currentBillingDocuments[existingIndex];
            const existingDocAny = existingDoc as any;
            const responseDataAny = response.data as any;
            currentBillingDocuments[existingIndex] = {
              ...existingDoc,
              ...response.data,
              // Preserve PatientPayment association if it exists in either document
              PatientPayment: responseDataAny.PatientPayment || existingDocAny.PatientPayment || responseDataAny.payment || existingDocAny.payment
            } as BillingDocument;
            this.billingDocumentsSubject.next([...currentBillingDocuments]);
          } else {
            // Add new document at the beginning
            // Ensure PatientPayment is accessible via both property names for compatibility
            const responseDataAny = response.data as any;
            const documentToAdd = {
              ...response.data,
              // Ensure payment is accessible via both PatientPayment and payment properties for statement generation
              payment: responseDataAny.PatientPayment || responseDataAny.payment
            } as any;
            this.billingDocumentsSubject.next([documentToAdd, ...currentBillingDocuments]);
          }
        }
      })
    );
  }

  // Upload PDF to billing document
  // replace retires the PDF and page images already filed on the document.
  uploadBillingDocumentPDF(billingDocumentId: string, groupId: string, pdfBlob: Blob, fileName: string, replace = false): Observable<BillingDocument> {
    const formData = new FormData();
    formData.append('newMedia0', pdfBlob, fileName);
    formData.append('groupId', groupId);
    if (replace) formData.append('replace', 'true');

    return withPdfPages$(formData).pipe(switchMap((body) => this.http.post<BillingDocument>(`${this.apiUrl}/billing-documents/${billingDocumentId}/upload-pdf`, body, {
      headers: this.getAuthHeaders()
    })));
  }

  convertEstimateToInvoice(billingDocumentId: string, dueDate?: Date): Observable<BillingDocument> {
    const payload: any = {};

    if (dueDate) {
      payload.dueDate = dueDate.toISOString();
    }

    // Get the group ID from the current group service
    const group = this.groupService.group;
    if (group) {
      payload.groupId = group.id;
    }

    return this.http.post<BillingDocument>(
      `${this.apiUrl}/billing-documents/${billingDocumentId}/convert-to-invoice`,
      payload,
      { headers: this.getAuthHeaders() }
    );
  }

  updateBillingDocument(billingDocumentId: string, updates: {
    notes?: string;
    dueDate?: Date;
    status?: string;
    pdfUrl?: string;
    pdfFileName?: string;
  }): Observable<BillingDocument> {
    return this.http.patch<BillingDocument>(
      `${this.apiUrl}/billing-documents/${billingDocumentId}`,
      updates,
      { headers: this.getAuthHeaders() }
    );
  }

  deleteBillingDocument(billingDocumentId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/billing-documents/${billingDocumentId}`, {
      headers: this.getAuthHeaders()
    });
  }

  getBillingDocument(billingDocumentId: string): Observable<BillingDocument> {
    return this.http.get<BillingDocument>(`${this.apiUrl}/billing-documents/${billingDocumentId}`, {
      headers: this.getAuthHeaders()
    });
  }

  getPatientBalance(patientId: string): Observable<{ success: boolean, data: PatientBalance }> {
    return this.http.get<{ success: boolean, data: PatientBalance }>(`${this.apiUrl}/billing-documents/patient/${patientId}/balance`, {
      headers: this.getAuthHeaders()
    }).pipe(
      tap(response => {
        this.patientBalanceSubject.next(response.data);
      })
    );
  }

  // Payment Methods
  loadPatientPayments(
    patientId: string,
    options?: {
      paymentMethod?: string;
      page?: number;
      pageSize?: number;
    }
  ): Observable<{ data: PatientPayment[]; pagination: any }> {
    const params: any = {};
    if (options?.paymentMethod) params.paymentMethod = options.paymentMethod;
    if (options?.page) params.page = options.page.toString();
    if (options?.pageSize) params.pageSize = options.pageSize.toString();

    return this.http.get<{ data: PatientPayment[]; pagination: any }>(
      `${this.apiUrl}/patient-payments/patient/${patientId}`,
      { params, headers: this.getAuthHeaders() }
    ).pipe(
      tap(response => {
        this.paymentsSubject.next(response.data);
      })
    );
  }

  createPayment(data: {
    patientId: string;
    groupId: string;
    paymentMethod: string;
    amountReceived: number;
    reference?: string;
    notes?: string;
    yocoPaymentId?: string;
    allocations?: Array<{
      billingDocumentId: string;
      amount: number;
    }>;
  }): Observable<PatientPayment> {
    return this.http.post<PatientPayment>(`${this.apiUrl}/patient-payments`, data, {
      headers: this.getAuthHeaders()
    });
  }

  updatePayment(paymentId: string, updates: {
    reference?: string;
    notes?: string;
    receiptUrl?: string;
    receiptFileName?: string;
  }): Observable<PatientPayment> {
    return this.http.patch<PatientPayment>(
      `${this.apiUrl}/patient-payments/${paymentId}`,
      updates,
      { headers: this.getAuthHeaders() }
    );
  }

  // Pays an invoice from the patient's credit; amount left out applies as much as the invoice can take.
  applyPatientCredit(billingDocumentId: string, groupId: string, amount?: number): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/patient-payments/apply-credit`,
      { groupId, billingDocumentId, amount },
      { headers: this.getAuthHeaders() }
    );
  }

  // Swaps one money payment method for another; the response carries the invoices and receipts to redraw.
  changePaymentMethod(paymentId: string, groupId: string, paymentMethod: string): Observable<any> {
    return this.http.patch(
      `${this.apiUrl}/patient-payments/${paymentId}/method`,
      { groupId, paymentMethod },
      { headers: this.getAuthHeaders() }
    );
  }

  deletePayment(paymentId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/patient-payments/${paymentId}`, {
      headers: this.getAuthHeaders()
    });
  }

  getPayment(paymentId: string): Observable<PatientPayment> {
    return this.http.get<PatientPayment>(`${this.apiUrl}/patient-payments/${paymentId}`, {
      headers: this.getAuthHeaders()
    });
  }

  getPatientPaymentSummary(patientId: string, groupId: string): Observable<PaymentSummary> {
    return this.http.get<PaymentSummary>(`${this.apiUrl}/patient-payments/patient/${patientId}/summary?groupId=${groupId}`, {
      headers: this.getAuthHeaders()
    });
  }

  // Helper method to format currency
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount);
  }

  // Helper method to format date
  formatDate(date: Date | string): string {
    return new Date(date).toLocaleDateString('en-ZA');
  }

  // Helper method to get document type label
  getDocumentTypeLabel(documentType: string): string {
    const labels: { [key: string]: string } = {
      'estimate': 'Estimate',
      'invoice': 'Invoice',
      'receipt': 'Receipt'
    };
    return labels[documentType] || documentType;
  }

  // Helper method to get status label
  getStatusLabel(status: string): string {
    const labels: { [key: string]: string } = {
      'draft': 'Draft',
      'pending': 'Pending',
      'paid': 'Paid',
      'partially_paid': 'Partially Paid',
      'overdue': 'Overdue',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status;
  }

  // Email Methods
  sendBillingDocumentEmail(
    billingDocumentId: string,
    recipientName: string,
    recipientEmail: string
  ): Observable<any> {
    const payload = {
      recipientName,
      recipientEmail
    };

    return this.http.post<any>(
      `${this.apiUrl}/billing-documents/${billingDocumentId}/send-email`,
      payload,
      { headers: this.getAuthHeaders() }
    );
  }

  // Get billing documents by date range for calendar view
  getBillingDocumentsByDateRange(
    groupId: string,
    startDate: string,
    endDate: string
  ): Observable<BillingDocument[]> {
    const params = {
      groupId,
      startDate,
      endDate
    };

    return this.http.get<BillingDocument[]>(`${this.apiUrl}/billing-documents/date-range`, {
      params,
      headers: this.getAuthHeaders()
    });
  }

  // Remove billing document (mark as removed)
  removeBillingDocument(billingDocumentId: string, reason: string): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/billing-documents/${billingDocumentId}/remove`, {
      reason: reason
    }, {
      headers: this.getAuthHeaders()
    });
  }

  // Remove billing document with payments (dangerous action)
  removeBillingDocumentWithPayments(billingDocumentId: string, reason: string): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/billing-documents/${billingDocumentId}/remove-with-payments`, {
      reason: reason
    }, {
      headers: this.getAuthHeaders()
    });
  }
}
